import { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  useBookingsByIdsNavigation,
  useRidesByIdsNavigation,
} from "@ryde/redux";
import { safeNumber } from "@ryde/shared";
import { blueMui, orangeMui, rydeColors } from "@ryde/theme";

import { PreviewDrawerLineStyled } from "../../misc";
import { ProductionHubClickableCount } from "./parts";
import { ProductionHubStatisticsWrapper } from "./production-hub-statistics-lines.styled";

interface IProductionHubStatisticsLinesProps {
  hubBookingIds?: number[];
  hubBookingCount?: number;
  nonHubBookingCount?: number;
  hubMainRideIds?: number[];
  hubMainRideCount?: number;
  hubPersonalRideIds?: number[];
  hubPersonalRideCount?: number;
  hubExpectedMainRideCount?: number;
  hubExpectedPersonalRideCount?: number;
  hubBookingsSkippedInSplitIds?: number[];
  hubBookingsSkippedInSplitCount?: number;
  hubPersonalGeoFailedBookingIds?: number[];
  hubPersonalGeoFailedCount?: number;
  hubBookingsWithMainSegmentIds?: number[];
  hubBookingsWithMainSegmentCount?: number;
  hubIncompletePairingBookingIds?: number[];
  hubIncompletePairingCount?: number;
  hubBookingsProducedIds?: number[];
  hubBookingsProducedCount?: number;
  hubCompanionBookingIds?: number[];
  hubCompanionBookingCount?: number;
  hubBookingsExcludedFromJoinIds?: number[];
  hubExplicitSkipBookingIds?: number[];
  hubExplicitSkipBookingCount?: number;
}

export const ProductionHubStatisticsLines: FC<
  IProductionHubStatisticsLinesProps
> = ({
  hubBookingIds,
  hubBookingCount,
  nonHubBookingCount,
  hubMainRideIds,
  hubMainRideCount,
  hubPersonalRideIds,
  hubPersonalRideCount,
  hubExpectedMainRideCount,
  hubExpectedPersonalRideCount,
  hubBookingsSkippedInSplitIds,
  hubBookingsSkippedInSplitCount,
  hubPersonalGeoFailedBookingIds,
  hubPersonalGeoFailedCount,
  hubBookingsWithMainSegmentIds,
  hubBookingsWithMainSegmentCount,
  hubIncompletePairingBookingIds,
  hubIncompletePairingCount,
  hubBookingsProducedIds,
  hubBookingsProducedCount,
  hubCompanionBookingIds,
  hubCompanionBookingCount,
  hubBookingsExcludedFromJoinIds,
  hubExplicitSkipBookingIds,
  hubExplicitSkipBookingCount,
}) => {
  const { t } = useTranslation();
  const { navigateToRidesByIds } = useRidesByIdsNavigation();
  const { navigateToBookingsByIds } = useBookingsByIdsNavigation();

  const mainRideCount = safeNumber(hubMainRideCount);
  const personalRideCount = safeNumber(hubPersonalRideCount);
  const expectedMain = safeNumber(hubExpectedMainRideCount);
  const expectedPersonal = safeNumber(hubExpectedPersonalRideCount);
  const skippedCount = safeNumber(hubBookingsSkippedInSplitCount);
  const mainSegmentCount =
    hubBookingsWithMainSegmentIds?.length ??
    safeNumber(hubBookingsWithMainSegmentCount);
  const geoFailedCount =
    hubPersonalGeoFailedBookingIds?.length ??
    safeNumber(hubPersonalGeoFailedCount);
  const incompleteCount = safeNumber(hubIncompletePairingCount);
  const producedCount =
    hubBookingsProducedIds?.length ?? safeNumber(hubBookingsProducedCount);
  const companionCount =
    hubCompanionBookingIds?.length ?? safeNumber(hubCompanionBookingCount);
  const joinExcludedCount = hubBookingsExcludedFromJoinIds?.length ?? 0;
  const explicitSkipCount =
    hubExplicitSkipBookingIds?.length ??
    safeNumber(hubExplicitSkipBookingCount);

  if (
    !safeNumber(hubBookingCount) &&
    !mainRideCount &&
    !personalRideCount &&
    !mainSegmentCount &&
    !skippedCount &&
    !geoFailedCount &&
    !incompleteCount &&
    !producedCount &&
    !companionCount &&
    !joinExcludedCount &&
    !explicitSkipCount
  ) {
    return null;
  }

  const mainMismatch = expectedMain > 0 && expectedMain !== mainRideCount;
  const personalMismatch =
    expectedPersonal > 0 && expectedPersonal !== personalRideCount;
  // Orange highlight when produced ride counts diverge from preview expected main/personal split.

  return (
    <ProductionHubStatisticsWrapper>
      <PreviewDrawerLineStyled>
        <span className="hub-statistics__title">
          {t("rideProduction.hubStatisticsTitle")}
        </span>
      </PreviewDrawerLineStyled>
      {Boolean(nonHubBookingCount) && (
        <PreviewDrawerLineStyled>
          <span>{t("rideProduction.hubNonHubBookings")}</span>
          <span>{nonHubBookingCount}</span>
        </PreviewDrawerLineStyled>
      )}
      {explicitSkipCount > 0 && (
        <PreviewDrawerLineStyled>
          <span>{t("rideProduction.hubExplicitSkip")}</span>
          <ProductionHubClickableCount
            ids={hubExplicitSkipBookingIds}
            count={explicitSkipCount}
            onNavigate={navigateToBookingsByIds}
          />
        </PreviewDrawerLineStyled>
      )}
      {safeNumber(hubBookingCount) > 0 && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.green}>
          <span>{t("rideProduction.hubBookingsCount")}</span>
          <ProductionHubClickableCount
            ids={hubBookingIds}
            count={safeNumber(hubBookingCount)}
            onNavigate={navigateToBookingsByIds}
          />
        </PreviewDrawerLineStyled>
      )}
      {expectedMain > 0 && (
        <PreviewDrawerLineStyled
          $numberColor={mainMismatch ? rydeColors.orange : undefined}
        >
          <span>{t("rideProduction.hubExpectedMainRides")}</span>
          <span>
            {mainRideCount} / {expectedMain}
          </span>
        </PreviewDrawerLineStyled>
      )}
      <PreviewDrawerLineStyled $numberColor={orangeMui._800}>
        <span className="hub-main-rides__label">
          {t("rideProduction.hubMainRides")}
        </span>
        <ProductionHubClickableCount
          ids={hubMainRideIds}
          count={mainRideCount}
          onNavigate={navigateToRidesByIds}
        />
      </PreviewDrawerLineStyled>
      {expectedPersonal > 0 && (
        <PreviewDrawerLineStyled
          $numberColor={personalMismatch ? rydeColors.orange : undefined}
        >
          <span>{t("rideProduction.hubExpectedPersonalRides")}</span>
          <span>
            {personalRideCount} / {expectedPersonal}
          </span>
        </PreviewDrawerLineStyled>
      )}
      <PreviewDrawerLineStyled $numberColor={orangeMui._800}>
        <span className="hub-personal-rides__label">
          {t("rideProduction.hubPersonalRides")}
        </span>
        <ProductionHubClickableCount
          ids={hubPersonalRideIds}
          count={personalRideCount}
          onNavigate={navigateToRidesByIds}
        />
      </PreviewDrawerLineStyled>
      {mainSegmentCount > 0 && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.green}>
          <span>{t("rideProduction.hubBookingsWithMainSegment")}</span>
          <ProductionHubClickableCount
            ids={hubBookingsWithMainSegmentIds}
            count={mainSegmentCount}
            onNavigate={navigateToBookingsByIds}
          />
        </PreviewDrawerLineStyled>
      )}
      {producedCount > 0 && (
        <PreviewDrawerLineStyled $numberColor={blueMui._800}>
          <span className="hub-bookings-routed__label">
            {t("rideProduction.hubBookingsProduced")}
          </span>
          <ProductionHubClickableCount
            ids={hubBookingsProducedIds}
            count={producedCount}
            onNavigate={navigateToBookingsByIds}
          />
        </PreviewDrawerLineStyled>
      )}
      {companionCount > 0 && (
        <PreviewDrawerLineStyled $numberColor={blueMui._800}>
          <span>{t("rideProduction.hubCompanionBookings")}</span>
          <ProductionHubClickableCount
            ids={hubCompanionBookingIds}
            count={companionCount}
            onNavigate={navigateToBookingsByIds}
          />
        </PreviewDrawerLineStyled>
      )}
      {skippedCount > 0 && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.orange}>
          <span>{t("rideProduction.hubBookingsSkippedInSplit")}</span>
          <ProductionHubClickableCount
            ids={hubBookingsSkippedInSplitIds}
            count={skippedCount}
            onNavigate={navigateToBookingsByIds}
          />
        </PreviewDrawerLineStyled>
      )}
      {geoFailedCount > 0 && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.orange}>
          <span>{t("rideProduction.hubPersonalGeoFailed")}</span>
          <ProductionHubClickableCount
            ids={hubPersonalGeoFailedBookingIds}
            count={geoFailedCount}
            onNavigate={navigateToBookingsByIds}
          />
        </PreviewDrawerLineStyled>
      )}
      {incompleteCount > 0 && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.orange}>
          <span>{t("rideProduction.hubIncompletePairing")}</span>
          <ProductionHubClickableCount
            ids={hubIncompletePairingBookingIds}
            count={incompleteCount}
            onNavigate={navigateToBookingsByIds}
          />
        </PreviewDrawerLineStyled>
      )}
      {joinExcludedCount > 0 && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.orange}>
          <span>{t("rideProduction.hubBookingsExcludedFromJoin")}</span>
          <ProductionHubClickableCount
            ids={hubBookingsExcludedFromJoinIds}
            count={joinExcludedCount}
            onNavigate={navigateToBookingsByIds}
          />
        </PreviewDrawerLineStyled>
      )}
    </ProductionHubStatisticsWrapper>
  );
};
